import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { buildQueryString } from '../../utils/queryStringBuilder';
import { RootState } from '../store';
import { FetchParams } from './employeesSlice';

export type AttendanceStatus = 'Present' | 'Absent' | 'On Leave' | 'Half Day' | 'Work From Home';

export interface Attendance {
    id: number;
    // Old structure - using attributes
    attributes?: {
        employee: {
            data: {
                id: number;
                attributes: {
                    Name: string;
                    lastName: string;
                }
            }
        };
        employee_name: string;
        status: AttendanceStatus;
        attendance_date: string;
        company: string;
        leave_type?: string;
        working_hours?: number;
        in_time?: string;
        out_time?: string;
        late_entry?: boolean;
        early_exit?: boolean;
        shift?: string;
        department?: string;
        remarks?: string;
        end_date?: string;
    };
    // New structure - direct properties (for the /attendance/employee endpoint)
    // These properties are marked as optional as not all attendance objects will have them
    employee?: {
        id: number;
        Name: string;
        lastName: string;
    };
    employee_name?: string;
    status?: AttendanceStatus;
    attendance_date?: string;
    company?: string;
    leave_type?: string;
    working_hours?: number;
    in_time?: string;
    out_time?: string;
    late_entry?: boolean;
    early_exit?: boolean;
    shift?: string;
    department?: string;
    remarks?: string;
    end_date?: string;
}

// Add interface for attendance statistics
export interface AttendanceStats {
    present: number;
    absent: number;
    onLeave: number;
    halfDay: number;
    workFromHome: number;
    totalWorkingHours: number;
    daysPresent: string[];
    daysAbsent: string[];
    daysOnLeave: string[];
    daysHalfDay: string[];
    daysWorkFromHome: string[];
}

interface AttendanceState {
    attendances: Attendance[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        pageSize: number;
        total: number;
    } | null;
    // Add stats to the state
    stats: AttendanceStats | null;
    isStatsLoading: boolean;
    statsError: string | null;
}

const initialState: AttendanceState = {
    attendances: [],
    isLoading: false,
    error: null,
    pagination: null,
    // Initialize stats state
    stats: null,
    isStatsLoading: false,
    statsError: null,
};

// Async thunk for fetching attendances
export const fetchAttendances = createAsyncThunk(
    'attendances/fetchAttendances',
    async (params: FetchParams = {}, { rejectWithValue }) => {
        try {
            const queryString = buildQueryString(params);
            const response = await api.get(`/api/attendances${queryString}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch attendances');
        }
    }
);

// Async thunk for creating attendance
export const createAttendance = createAsyncThunk(
    'attendances/createAttendance',
    async (attendanceData: {
        employee: number;
        employee_name: string;
        status: AttendanceStatus;
        attendance_date: string;
        company: string;
        leave_type?: string;
        working_hours?: number;
        in_time?: string;
        out_time?: string;
        late_entry?: boolean;
        early_exit?: boolean;
        shift?: string;
        department?: string;
        remarks?: string;
    }, { rejectWithValue }) => {
        try {
            // Format time values by combining date and time
            const formatTimeWithDate = (date: string, timeStr?: string) => {
                if (!timeStr) return undefined;
                
                // HTML time inputs are in 24-hour format: HH:MM
                const [hours, minutes] = timeStr.split(':').map(part => parseInt(part, 10));
                
                // Create date with correct time
                const dateObj = new Date(date);
                dateObj.setHours(hours);
                dateObj.setMinutes(minutes);
                dateObj.setSeconds(0);
                
                return dateObj.toISOString();
            };

            const formattedData = {
                ...attendanceData,
                attendance_date: new Date(attendanceData.attendance_date).toISOString(),
                in_time: formatTimeWithDate(attendanceData.attendance_date, attendanceData.in_time),
                out_time: formatTimeWithDate(attendanceData.attendance_date, attendanceData.out_time)
            };

            console.log('Sending formatted attendance data:', formattedData);
            
            const response = await api.post('/api/attendances', {
                data: formattedData
            });
            return response.data;
        } catch (error: any) {
            console.error('Error creating attendance:', error);
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to create attendance');
        }
    }
);

// Async thunk for updating attendance
export const updateAttendance = createAsyncThunk(
    'attendances/updateAttendance',
    async ({ id, data }: { id: number; data: Partial<Attendance['attributes']> }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/api/attendances/${id}`, {
                data
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to update attendance');
        }
    }
);

// Async thunk for fetching employee attendance statistics
export const fetchEmployeeAttendanceStats = createAsyncThunk(
    'attendances/fetchEmployeeAttendanceStats',
    async ({ 
        employeeId, 
        startDate, 
        endDate 
    }: { 
        employeeId: number, 
        startDate: string, 
        endDate: string 
    }, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/attendance/employee/${employeeId}`, {
                params: {
                    startDate,
                    endDate
                }
            });

            // Process the data to calculate statistics
            const attendanceData = response.data.data || [];
            const stats: AttendanceStats = {
                present: 0,
                absent: 0,
                onLeave: 0,
                halfDay: 0,
                workFromHome: 0,
                totalWorkingHours: 0,
                daysPresent: [],
                daysAbsent: [],
                daysOnLeave: [],
                daysHalfDay: [],
                daysWorkFromHome: []
            };
            
            attendanceData.forEach((record: any) => {
                // No attributes property - fields are directly on the record
                const date = record.attendance_date;
                
                switch(record.status) {
                    case 'Present':
                        stats.present++;
                        stats.daysPresent.push(date);
                        stats.totalWorkingHours += record.working_hours || 8;
                        break;
                    case 'Absent':
                        stats.absent++;
                        stats.daysAbsent.push(date);
                        break;
                    case 'On Leave':
                        stats.onLeave++;
                        stats.daysOnLeave.push(date);
                        break;
                    case 'Half Day':
                        stats.halfDay++;
                        stats.daysHalfDay.push(date);
                        stats.totalWorkingHours += record.working_hours || 4;
                        break;
                    case 'Work From Home':
                        stats.workFromHome++;
                        stats.daysWorkFromHome.push(date);
                        stats.totalWorkingHours += record.working_hours || 8;
                        break;
                    default:
                        console.warn(`Unhandled attendance status: ${record.status}`);
                        break;
                }
            });

            return { stats, data: attendanceData };
        } catch (error: any) {
            console.error('Error fetching employee attendance stats:', error);
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch employee attendance statistics');
        }
    }
);

const attendanceSlice = createSlice({
    name: 'attendances',
    initialState,
    reducers: {
        clearAttendanceError: (state) => {
            state.error = null;
        },
        clearAttendanceStats: (state) => {
            state.stats = null;
            state.statsError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch attendances
            .addCase(fetchAttendances.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAttendances.fulfilled, (state, action) => {
                state.isLoading = false;
                state.attendances = action.payload.data;
                state.pagination = action.payload.meta.pagination;
            })
            .addCase(fetchAttendances.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to fetch attendances';
            })
            // Create attendance
            .addCase(createAttendance.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createAttendance.fulfilled, (state, action) => {
                state.isLoading = false;
                state.attendances.push(action.payload.data);
            })
            .addCase(createAttendance.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to create attendance';
            })
            // Update attendance
            .addCase(updateAttendance.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateAttendance.fulfilled, (state, action) => {
                state.isLoading = false;
                const updatedAttendance = action.payload.data;
                const index = state.attendances.findIndex(a => a.id === updatedAttendance.id);
                if (index !== -1) {
                    state.attendances[index] = updatedAttendance;
                }
            })
            .addCase(updateAttendance.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to update attendance';
            })
            // Fetch employee attendance stats
            .addCase(fetchEmployeeAttendanceStats.pending, (state) => {
                state.isStatsLoading = true;
                state.statsError = null;
            })
            .addCase(fetchEmployeeAttendanceStats.fulfilled, (state, action) => {
                state.isStatsLoading = false;
                state.stats = action.payload.stats;
            })
            .addCase(fetchEmployeeAttendanceStats.rejected, (state, action) => {
                state.isStatsLoading = false;
                state.statsError = action.error.message || 'Failed to fetch attendance statistics';
            });
    }
});

export const { clearAttendanceError, clearAttendanceStats } = attendanceSlice.actions;

// Selectors
export const selectAttendances = (state: RootState) => state.attendances.attendances;
export const selectAttendanceLoading = (state: RootState) => state.attendances.isLoading;
export const selectAttendanceError = (state: RootState) => state.attendances.error;
export const selectAttendancePagination = (state: RootState) => state.attendances.pagination;
export const selectAttendanceStats = (state: RootState) => state.attendances.stats;
export const selectAttendanceStatsLoading = (state: RootState) => state.attendances.isStatsLoading;
export const selectAttendanceStatsError = (state: RootState) => state.attendances.statsError;

export default attendanceSlice.reducer; 